import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { VaModal } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { scrollAndFocus } from 'platform/utilities/scroll';
import { focusElement } from 'platform/utilities/ui';
import { useSessions, formatSignInTimestamp } from '../hooks';

const MOBILE_DEVICE_KEYWORDS = [
  'iphone',
  'ipad',
  'ios',
  'android',
  'galaxy',
  'pixel',
  'phone',
  'sm-', // Samsung model numbers, e.g. "Samsung SM-S918B"
  'mobile', // e.g. browser "Chrome Mobile"/"Edge Mobile"/"VAMobile", covers cases where device_description is too generic to tell (e.g. "K")
  'okhttp', // VA mobile app's Android HTTP client; browser="okhttp", device_description="Other" carries no other mobile signal
];

const GENERIC_DEVICE_NAMES = ['other'];
const ANDROID_GENERIC_DEVICE_NAMES = ['k', 'generic smartphone'];
const OS_ONLY_DEVICE_NAMES = ['linux'];
const ANDROID_CHROME_MOBILE_BROWSERS = [
  'chrome mobile',
  'chrome mobile webview',
];
const VA_MOBILE_APP_BROWSERS = ['okhttp', 'vamobile'];

function isMobileSession(deviceName, browser) {
  const normalizedDeviceName = deviceName?.toLowerCase();
  const normalizedBrowser = browser?.toLowerCase();
  if (VA_MOBILE_APP_BROWSERS.includes(normalizedBrowser)) return true;
  if (ANDROID_GENERIC_DEVICE_NAMES.includes(normalizedDeviceName)) return true;
  if (normalizedDeviceName?.includes('sm-')) return true;
  if (ANDROID_CHROME_MOBILE_BROWSERS.includes(normalizedBrowser)) return true;
  const combined = `${deviceName || ''} ${browser || ''}`.trim().toLowerCase();
  return MOBILE_DEVICE_KEYWORDS.some(keyword => combined.includes(keyword));
}

function getDeviceIconName(deviceName, browser) {
  return isMobileSession(deviceName, browser)
    ? 'phone_iphone'
    : 'laptop_windows';
}

function getDeviceDisplayName(deviceName, browser) {
  if (VA_MOBILE_APP_BROWSERS.includes(browser?.toLowerCase())) {
    return 'VA mobile app';
  }
  if (ANDROID_GENERIC_DEVICE_NAMES.includes(deviceName?.toLowerCase())) {
    return 'Android device';
  }
  if (!deviceName || GENERIC_DEVICE_NAMES.includes(deviceName.toLowerCase())) {
    return 'Device';
  }
  if (deviceName.toLowerCase().includes('sm-')) {
    return deviceName.toUpperCase();
  }
  if (ANDROID_CHROME_MOBILE_BROWSERS.includes(browser?.toLowerCase())) {
    return deviceName.toLowerCase() === 'pixel 6'
      ? 'Pixel 6'
      : 'Android device';
  }
  return deviceName;
}

function getModalDeviceName(deviceName, browser) {
  const displayName = getDeviceDisplayName(deviceName, browser);
  return OS_ONLY_DEVICE_NAMES.includes(deviceName?.toLowerCase())
    ? `${displayName} device`
    : displayName;
}

function getBrowserDisplayName(browser) {
  if (VA_MOBILE_APP_BROWSERS.includes(browser?.toLowerCase())) {
    return 'VA mobile app';
  }
  return browser;
}

function DeviceIcon({ deviceName, browser, signedOut }) {
  return (
    <va-icon
      icon={getDeviceIconName(deviceName, browser)}
      size={3}
      class={
        signedOut
          ? 'vads-u-color--base-dark signed-in-devices-widget__device-icon'
          : 'signed-in-devices-widget__device-icon'
      }
      style={{ visibility: 'visible' }}
    />
  );
}

DeviceIcon.propTypes = {
  browser: PropTypes.string,
  deviceName: PropTypes.string,
  signedOut: PropTypes.bool,
};

DeviceIcon.defaultProps = {
  browser: null,
  deviceName: null,
  signedOut: false,
};

function DeviceCard({ session, onRequestSignOut }) {
  const {
    current,
    deviceName,
    credentialType,
    browser,
    signInDisplay,
    location,
    isSignedOut,
    signedOutAt,
  } = session;
  const signedOutDisplay = isSignedOut
    ? formatSignInTimestamp(signedOutAt)
    : null;
  const hasSignInDetails =
    credentialType || browser || signInDisplay || location;
  const hasSignedOutDetails =
    credentialType || browser || signedOutDisplay || location;

  return (
    <va-card
      class="signed-in-devices-widget__card vads-u-margin-bottom--2"
      style={
        isSignedOut ? { borderColor: 'var(--vads-color-base-darker)' } : null
      }
    >
      <div className="vads-u-display--flex vads-u-align-items--center vads-u-margin-bottom--1">
        <DeviceIcon
          deviceName={deviceName}
          browser={browser}
          signedOut={isSignedOut}
        />
        <h3
          className={`vads-u-margin--0 vads-u-margin-left--0p5${
            isSignedOut ? ' vads-u-color--base-dark' : ''
          }`}
        >
          {getDeviceDisplayName(deviceName, browser)}
        </h3>
      </div>
      {current && (
        <div className="vads-u-display--flex vads-u-align-items--center vads-u-margin-bottom--1">
          <va-icon icon="check_circle" size={2} class="vads-u-color--green" />
          <span className="vads-u-font-size--sm vads-u-font-weight--bold vads-u-margin-left--0p5">
            CURRENT DEVICE
          </span>
        </div>
      )}
      {isSignedOut && (
        <div className="vads-u-display--flex vads-u-align-items--center vads-u-margin-bottom--1">
          <va-icon
            icon="remove_circle"
            size={2}
            class="vads-u-color--base-dark vads-u-margin-right--0p5"
          />
          <span className="vads-u-font-size--sm vads-u-font-weight--bold vads-u-color--base-darker">
            SIGNED OUT
          </span>
        </div>
      )}
      {isSignedOut && hasSignedOutDetails && (
        <p className="vads-u-margin-y--1 vads-u-color--base-darker">
          Signed out
          {credentialType && (
            <>
              {' '}
              of <strong>{credentialType}</strong>
            </>
          )}
          {browser && <> on {getBrowserDisplayName(browser)}</>}
          {signedOutDisplay && <> on {signedOutDisplay}</>}
          {location && <> in {location}</>}
        </p>
      )}
      {!isSignedOut && hasSignInDetails && (
        <p className="vads-u-margin-y--1">
          Signed in
          {credentialType && (
            <>
              {' '}
              with <strong>{credentialType}</strong>
            </>
          )}
          {browser && <> on {getBrowserDisplayName(browser)}</>}
          {signInDisplay && <> on {signInDisplay}</>}
          {location && <> in {location}</>}
        </p>
      )}
      {!current && !isSignedOut && (
        <va-button
          text="Sign out"
          label={`Sign out of your ${getDeviceDisplayName(
            deviceName,
            browser,
          )}${signInDisplay ? ` signed in on ${signInDisplay}` : ''}`}
          secondary
          onClick={() => onRequestSignOut(session)}
        />
      )}
    </va-card>
  );
}

DeviceCard.propTypes = {
  session: PropTypes.shape({
    browser: PropTypes.string,
    credentialType: PropTypes.string,
    current: PropTypes.bool,
    deviceName: PropTypes.string,
    handle: PropTypes.string.isRequired,
    isSignedOut: PropTypes.bool,
    location: PropTypes.string,
    signInDisplay: PropTypes.string,
    signedOutAt: PropTypes.string,
  }).isRequired,
  onRequestSignOut: PropTypes.func.isRequired,
};

export default function SignedInDevices() {
  const {
    sessions,
    isLoading,
    error,
    revokeSession,
    revokeAllOtherSessions,
    markSessionsSignedOut,
  } = useSessions();
  const [actionError, setActionError] = useState(null);
  const [successAlert, setSuccessAlert] = useState(null);
  const [sessionPendingSignOut, setSessionPendingSignOut] = useState(null);
  const [signOutAllModalVisible, setSignOutAllModalVisible] = useState(false);

  useEffect(() => {
    document.title = 'Signed-In Devices | Veterans Affairs';
    focusElement('[data-focus-target]');
  }, []);

  useEffect(() => {
    if (successAlert) scrollAndFocus('va-alert[status="success"]');
  }, [successAlert]);

  useEffect(() => {
    if (actionError) scrollAndFocus('va-alert[status="error"]');
  }, [actionError]);

  const handleConfirmSignOut = async () => {
    const session = sessionPendingSignOut;
    setSessionPendingSignOut(null);
    setActionError(null);
    setSuccessAlert(null);
    try {
      await revokeSession(session.handle);
      markSessionsSignedOut([session.handle]);
      setSuccessAlert({
        type: 'single',
        deviceName: session.deviceName,
        browser: session.browser,
      });
    } catch (err) {
      setActionError(err);
    }
  };

  const handleConfirmSignOutAll = async () => {
    setSignOutAllModalVisible(false);
    setActionError(null);
    setSuccessAlert(null);
    const handlesToSignOut = sessions
      .filter(session => !session.current && !session.isSignedOut)
      .map(session => session.handle);
    try {
      await revokeAllOtherSessions();
      markSessionsSignedOut(handlesToSignOut);
      setSuccessAlert({ type: 'all' });
    } catch (err) {
      setActionError(err);
    }
  };

  let content;
  if (isLoading) {
    content = <va-loading-indicator message="Loading your sessions..." />;
  } else if (error) {
    content = (
      <va-alert status="error" visible>
        <h2 slot="headline">We couldn’t load your sessions</h2>
        <p>Please try again later.</p>
      </va-alert>
    );
  } else if (!sessions.length) {
    content = (
      <va-alert status="info" visible>
        <h2 slot="headline">No active sessions</h2>
        <p>You don’t have any other active sessions on VA.gov.</p>
      </va-alert>
    );
  } else {
    const pendingDeviceName = sessionPendingSignOut?.deviceName
      ? `your ${getModalDeviceName(
          sessionPendingSignOut.deviceName,
          sessionPendingSignOut.browser,
        )}`
      : 'this device';
    const activeSessionCount = sessions.filter(
      session => !session.isSignedOut,
    ).length;
    const sessionRank = session => {
      if (session.current) return 0;
      if (!session.isSignedOut) return 1;
      return 2;
    };
    const sortedSessions = [...sessions].sort(
      (a, b) => sessionRank(a) - sessionRank(b),
    );

    content = (
      <>
        {successAlert &&
          (successAlert.type === 'all' ? (
            <va-alert status="success" visible>
              <h2 slot="headline">
                Successfully signed out on all other devices
              </h2>
              <p>You signed out of VA.gov on all other devices.</p>
            </va-alert>
          ) : (
            <va-alert status="success" visible>
              <h2 slot="headline">
                Successfully signed out
                {successAlert.deviceName
                  ? ` of your ${getDeviceDisplayName(
                      successAlert.deviceName,
                      successAlert.browser,
                    )}`
                  : ''}
              </h2>
              <p>
                You signed out of VA.gov
                {successAlert.deviceName
                  ? ` on your ${getDeviceDisplayName(
                      successAlert.deviceName,
                      successAlert.browser,
                    )}`
                  : ''}
                .
              </p>
            </va-alert>
          ))}
        {actionError && (
          <va-alert status="error" visible>
            <h2 slot="headline">Sorry, we could not sign you out</h2>
            <p>Please try again later.</p>
          </va-alert>
        )}

        {activeSessionCount > 1 && (
          <>
            <h2 className="vads-u-margin-top--3">Sign out on all devices</h2>
            <p>
              You can sign out of VA.gov on all other devices. You’ll stay
              signed in on the device you’re using right now.
            </p>
            <va-button
              text="Sign out on all devices"
              onClick={() => setSignOutAllModalVisible(true)}
            />
          </>
        )}

        <h2 className="vads-u-margin-top--3">Your signed-in devices</h2>
        {sortedSessions.map(session => (
          <DeviceCard
            key={session.handle}
            session={session}
            onRequestSignOut={setSessionPendingSignOut}
          />
        ))}

        <h2>Don’t recognize one of these devices?</h2>
        <p>
          To secure your account, you can sign out everywhere right now. Then
          change your password with <strong>ID.me</strong> or{' '}
          <strong>Login.gov</strong>.
        </p>
        <va-link
          href="https://www.va.gov/resources/can-i-call-va-to-reset-my-logingov-or-idme-password/"
          text="Learn how to update your password to secure your account"
        />

        <VaModal
          status="warning"
          modalTitle={`Sign out of ${pendingDeviceName}?`}
          visible={!!sessionPendingSignOut}
          onCloseEvent={() => setSessionPendingSignOut(null)}
          onPrimaryButtonClick={handleConfirmSignOut}
          onSecondaryButtonClick={() => setSessionPendingSignOut(null)}
          primaryButtonText="Yes, sign out"
          secondaryButtonText="No, go back"
        >
          <p>
            This will sign you out of VA.gov on {pendingDeviceName}. You’ll stay
            signed in on your current device.
          </p>
        </VaModal>

        <VaModal
          status="warning"
          modalTitle="Sign out on all devices?"
          visible={signOutAllModalVisible}
          onCloseEvent={() => setSignOutAllModalVisible(false)}
          onPrimaryButtonClick={handleConfirmSignOutAll}
          onSecondaryButtonClick={() => setSignOutAllModalVisible(false)}
          primaryButtonText="Yes, sign out"
          secondaryButtonText="No, go back"
        >
          <p>
            This will sign you out of VA.gov on all other devices. You’ll stay
            signed in on your current device.
          </p>
        </VaModal>
      </>
    );
  }

  return (
    <>
      <h1 tabIndex="-1" className="vads-u-margin-bottom--2" data-focus-target>
        Signed-in devices
      </h1>
      <p>
        Manage where you’re signed in to VA.gov to help keep your account
        secure.
      </p>
      {content}
    </>
  );
}
